import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import authApi from '../services/api/auth';
const AuthContext = createContext(undefined);
const AUTH_ROLE_STORAGE_KEY = 'wt_active_role';
function isActiveRole(value) {
    return ['reader', 'contributor', 'screener', 'reviewer', 'admin'].includes(String(value || ''));
}
function getAvailableRolesForUser(user) {
    const roles = ['reader'];
    const isAdmin = Boolean(user?.roles?.admin);
    const contributorReady = isAdmin || user?.onboarding?.contributor?.completed !== false;
    const reviewerReady = isAdmin || user?.onboarding?.reviewer?.completed !== false;
    if (contributorReady)
        roles.push('contributor');
    if (user?.roles?.screener)
        roles.push('screener');
    if (user?.roles?.reviewer && reviewerReady)
        roles.push('reviewer');
    if (user?.roles?.admin)
        roles.push('admin');
    return roles;
}
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeRole, setActiveRoleState] = useState(() => {
        try {
            const stored = localStorage.getItem(AUTH_ROLE_STORAGE_KEY);
            if (isActiveRole(stored)) {
                return stored;
            }
        }
        catch { /* ignore */ }
        return 'contributor';
    });
    const availableRoles = useMemo(() => getAvailableRolesForUser(user), [user]);
    const setActiveRole = useCallback((role) => {
        setActiveRoleState(role);
        try {
            localStorage.setItem(AUTH_ROLE_STORAGE_KEY, role);
        }
        catch {
            // ignore storage failures
        }
        if (user) {
            void authApi.roleSwitch(role).catch((error) => {
                console.error('Role switch sync failed:', error);
            });
        }
    }, [user]);
    const checkAuthStatus = useCallback(async () => {
        try {
            const response = await authApi.me();
            const resolvedUser = response.user || null;
            setUser(resolvedUser);
            const allowedRoles = getAvailableRolesForUser(resolvedUser);
            const storedRole = (() => {
                try {
                    return localStorage.getItem(AUTH_ROLE_STORAGE_KEY);
                }
                catch {
                    return null;
                }
            })();
            const normalizedServerRole = isActiveRole(response.activeRole) ? response.activeRole : null;
            const nextRole = (normalizedServerRole && allowedRoles.includes(normalizedServerRole))
                ? normalizedServerRole
                : (storedRole && isActiveRole(storedRole) && allowedRoles.includes(storedRole) ? storedRole : 'contributor');
            setActiveRoleState(nextRole);
        }
        catch (error) {
            console.error('Error checking auth status:', error);
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    useEffect(() => {
        void checkAuthStatus();
    }, [checkAuthStatus]);
    const login = useCallback(async (username, password, rememberMe) => {
        try {
            const data = await authApi.login({
                username,
                password,
                ...(typeof rememberMe === 'boolean' ? { rememberMe } : {}),
            });
            const resolvedUser = data.user || null;
            setUser(resolvedUser);
            const allowedRoles = getAvailableRolesForUser(resolvedUser);
            const serverRole = isActiveRole(data.activeRole) ? data.activeRole : null;
            const nextRole = serverRole && allowedRoles.includes(serverRole) ? serverRole : 'contributor';
            setActiveRoleState(nextRole);
        }
        catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    }, []);
    const signup = useCallback(async (username, email, password, recaptchaResponse, rememberMe) => {
        try {
            const data = await authApi.signup({
                username,
                email,
                password,
                recaptchaResponse,
                ...(typeof rememberMe === 'boolean' ? { rememberMe } : {}),
            });
            const resolvedUser = data.user || null;
            setUser(resolvedUser);
            const allowedRoles = getAvailableRolesForUser(resolvedUser);
            const serverRole = isActiveRole(data.activeRole) ? data.activeRole : null;
            const nextRole = serverRole && allowedRoles.includes(serverRole) ? serverRole : 'contributor';
            setActiveRoleState(nextRole);
        }
        catch (error) {
            console.error('Signup error:', error);
            throw error;
        }
    }, []);
    const logout = useCallback(async () => {
        try {
            await authApi.logout();
            setUser(null);
        }
        catch (error) {
            console.error('Logout error:', error);
            throw error;
        }
    }, []);
    const updateUser = useCallback((userData) => {
        setUser((current) => (current ? { ...current, ...userData } : current));
    }, []);
    const value = useMemo(() => ({
        user,
        isAuthenticated: !!user,
        isLoading,
        activeRole,
        setActiveRole,
        availableRoles,
        signup,
        login,
        logout,
        refreshAuth: checkAuthStatus,
        updateUser,
    }), [user, isLoading, activeRole, setActiveRole, availableRoles, signup, login, logout, checkAuthStatus, updateUser]);
    return React.createElement(AuthContext.Provider, { value: value }, children);
};
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
export default AuthContext;
